import { AsyncPipe, NgClass, NgStyle, ViewportScroller } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  NgZone,
  OnInit,
  ViewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { RouterOutlet, Scroll } from '@angular/router';
import { debounceTime, distinctUntilChanged, map, tap } from 'rxjs';
import { StructuredDataKeys } from './shared/enums/structured-data-keys.enum';
import { Metadata } from './shared/models/sys/metadata';
import { Schema } from './shared/models/sys/schema-org';
import { Loader } from './shared/models/ui/loader';
import { AppRouterService } from './shared/services/app-router.service';
import { AppStateService } from './shared/services/app-state.service';
import { ResponsiveService } from './shared/services/html/responsive.service';
import { ScrollerService } from './shared/services/html/scroller.service';
import { SeoService } from './shared/services/html/seo.service';
import { StructuredDataService } from './shared/services/html/structured-data.service';
import { BotChatService } from './shared/services/libs/bot-chat.service';
import { ChatService } from './shared/services/libs/chat.service';
import { PromoService } from './shared/services/libs/promo.service';
import { RecaptchaService } from './shared/services/libs/recaptcha.service';
import { TagManagerService } from './shared/services/tms/tag-manager.service';
import { TrackerService } from './shared/services/tms/tracker.service';
import { nextFrame } from './shared/utils/rxjs.utils';
import { BotChatComponent } from './shared/components/ui/bot-chat/bot-chat.component';
import { LoaderComponent } from './shared/components/ui/loader/loader.component';
import { SnackbarComponent } from './shared/components/ui/snackbar/snackbar.component';
import { IconComponent } from './shared/components/ui/icon/icon.component';
import { ButtonComponent } from './shared/components/ui/button/button.component';
import { TranslatePipe } from './shared/pipes/translate.pipe';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    RouterOutlet,
    NgClass,
    NgStyle,
    AsyncPipe,
    LoaderComponent,
    SnackbarComponent,
    IconComponent,
    ButtonComponent,
    BotChatComponent,
    TranslatePipe,
  ],
})
export class AppComponent implements OnInit, AfterViewInit {
  private readonly _appState: AppStateService = inject(AppStateService);
  private readonly _appRouter: AppRouterService = inject(AppRouterService);
  private readonly _responsive: ResponsiveService = inject(ResponsiveService);
  private readonly _seo: SeoService = inject(SeoService);
  private readonly _tagManager: TagManagerService = inject(TagManagerService);
  private readonly _tracker: TrackerService = inject(TrackerService);
  private readonly _structuredData: StructuredDataService = inject(
    StructuredDataService
  );
  private readonly _scroller: ScrollerService = inject(ScrollerService);
  private readonly _chat: ChatService = inject(ChatService);
  private readonly _promo: PromoService = inject(PromoService);
  private readonly _botChat: BotChatService = inject(BotChatService);
  private readonly _recaptcha: RecaptchaService = inject(RecaptchaService);
  private readonly _ngZone: NgZone = inject(NgZone);
  private readonly _viewportScroller: ViewportScroller =
    inject(ViewportScroller);

  public loading: boolean = true;
  public push: boolean = true;
  public chat: boolean = false;
  public botChat: boolean = false;

  @ViewChild('router') public routerElement?: ElementRef<HTMLElement>;

  public ngOnInit(): void {
    this._scroller.bootstrap();
    this._seo.bootstrap();
    this._tagManager.bootstrap();
    this._structuredData.bootstrap();
    this._chat.bootstrap();
    this._promo.bootstrap();
    this._recaptcha.bootstrap();
    this._botChat.bootstrap();

    this.observeState();
    this.observeNavigation();

    this._chat.active$.subscribe((active: boolean) => {
      this.chat = active;
    });

    this._botChat.available$.subscribe((available: boolean) => {
      this.botChat = available;
    });
  }

  public ngAfterViewInit(): void {
    // We wait for the view to be initialized to bootstrap the responsive service
    this._responsive.bootstrap(this.routerElement?.nativeElement);
  }

  private observeState(): void {
    // Update loader
    this._appState.loader$.pipe(nextFrame()).subscribe((loader: Loader) =>
      this._ngZone.run(() => {
        // Sometime we lose angular scope ¯\_(ツ)_/¯
        this.loading = !!(loader.queue || loader.forced);
      })
    );

    // Responsive variabels update (we delay the updates to make sure the push header is updated)
    this._appState.push$
      .pipe(
        map((push: boolean) => push),
        distinctUntilChanged(),
        nextFrame(),
        tap((push: boolean) => (this.push = push)),
        debounceTime(50)
      )
      .subscribe((push: boolean) => {
        this._responsive.refresh();
      });
  }

  private observeNavigation(): void {
    this._viewportScroller.setHistoryScrollRestoration('manual');
    this._appRouter.navigationScroll$.subscribe((scroll: Scroll) => {
      if (scroll.position) {
        // history navigation
        this._scroller.scrollToPosition(scroll.position, true);
      } else if (scroll.anchor) {
        // anchor navigation
        this._scroller.scrollToAnchor(scroll.anchor, true);
      } else {
        // forward navigation
        this._scroller.scrollTop(true);
      }
    });

    // We finally listen pour page load to track events
    let schemas: { [key in StructuredDataKeys]?: Schema } | undefined;
    this._appRouter.metadata$.subscribe((metadata?: Metadata) => {
      this._seo.title = metadata?.title;
      this._seo.description = metadata?.description;
      this._seo.keywords = metadata?.keywords;
      this._seo.author = metadata?.author;
      this._seo.image = metadata?.image;
      this._seo.robots = metadata?.robots;
      this._seo.setAlternateLinks(metadata?.links ?? []);
      this._seo.setCanonicalURL(metadata?.canonical);

      // Update the page structured data
      Object.keys(metadata?.schemas ?? {}).forEach((value: string) => {
        const key: StructuredDataKeys = value as unknown as StructuredDataKeys;
        if (metadata?.schemas?.[key]) {
          this._structuredData.set(key, metadata.schemas[key]);
        }
      });

      // Cleanup the missing old one
      Object.keys(schemas ?? {}).forEach((value: string) => {
        const key: StructuredDataKeys | undefined = !Object.keys(
          metadata?.schemas ?? {}
        ).includes(value)
          ? (value as unknown as StructuredDataKeys)
          : undefined;

        if (key) {
          this._structuredData.delete(key);
        }
      });
      schemas = metadata?.schemas;

      if (metadata) {
        this._tracker.trackPage();
        this._tracker.trackPreload(metadata.type);
      }
    });
  }

  public startChat(): void {
    this._chat.toggle();
  }
}
